import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/adminlayout';
import { PRODUCTION_URL } from '../productionLink/productionLink';
import './reservations.css';

const Reservations = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('list'); // 'list' or 'new'
    const [loading, setLoading] = useState(false);
    const [rooms, setRooms] = useState([]);
    const [availableRooms, setAvailableRooms] = useState([]);
    const [reservations, setReservations] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [filterStatus, setFilterStatus] = useState('upcoming'); // 'upcoming', 'all', 'confirmed', 'checked_in'
    const [searchQuery, setSearchQuery] = useState('');

    // Modal states
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [paymentData, setPaymentData] = useState({ amount: '', paymentMethod: 'cash', notes: '' });
    const [cancelData, setCancelData] = useState({ reason: '', refundMethod: 'cash', processRefund: true });

    const [formData, setFormData] = useState({
        // Guest Details
        guestName: '',
        guestPhone: '',
        guestEmail: '',
        guestNationality: 'Indian',
        guestIdType: 'aadhar',
        guestIdNumber: '',
        numberOfGuests: 1,

        // Booking Details
        roomId: '',
        checkInDate: '',
        checkOutDate: '',
        bookingType: 'walk_in',

        // Payment Details
        pricePerNight: 0,
        totalNights: 0,
        baseAmount: 0,
        gstPercentage: 5,
        gstAmount: 0,
        totalAmount: 0,
        advancePayment: 0,
        paymentMethod: 'cash',

        // Additional
        specialRequests: '',
        isComplementary: false
    });

    const idTypes = [
        { value: 'aadhar', label: 'Aadhar Card' },
        { value: 'passport', label: 'Passport' },
        { value: 'driving_license', label: 'Driving License' },
        { value: 'voter_id', label: 'Voter ID' },
        { value: 'pan', label: 'PAN Card' }
    ];

    const bookingTypes = [
        { value: 'walk_in', label: 'Walk-in' },
        { value: 'online', label: 'Online Booking' },
        { value: 'phone', label: 'Phone Reservation' },
        { value: 'corporate', label: 'Corporate' },
        { value: 'travel_agent', label: 'Travel Agent' },
        { value: 'ota', label: 'OTA (Online Travel Agency)' }
    ];

    const paymentMethods = [
        { value: 'cash', label: 'Cash' },
        { value: 'card', label: 'Credit/Debit Card' },
        { value: 'upi', label: 'UPI' },
        { value: 'bank_transfer', label: 'Bank Transfer' },
        { value: 'pay_later', label: 'Pay at Check-in' }
    ];

    useEffect(() => {
        fetchRooms();
        fetchReservations();
    }, []);

    useEffect(() => {
        if (formData.checkInDate && formData.checkOutDate) {
            checkAvailability();
            calculatePricing();
        }
    }, [formData.checkInDate, formData.checkOutDate, formData.roomId]);

    useEffect(() => {
        if (successMessage || errorMessage) {
            const timer = setTimeout(() => {
                setSuccessMessage('');
                setErrorMessage('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [successMessage, errorMessage]);

    const fetchRooms = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${PRODUCTION_URL}/api/admin/rooms`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setRooms(data.data);
            }
        } catch (err) {
            console.error('Error fetching rooms:', err);
        }
    };

    const fetchReservations = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${PRODUCTION_URL}/api/admin/bookings`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setReservations(data.data || []);
            }
        } catch (err) {
            console.error('Error fetching reservations:', err);
        } finally {
            setLoading(false);
        }
    };

    const checkAvailability = async () => {
        if (!formData.checkInDate || !formData.checkOutDate) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${PRODUCTION_URL}/api/admin/rooms/available?checkIn=${formData.checkInDate}&checkOut=${formData.checkOutDate}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const data = await response.json();
            if (data.success) {
                setAvailableRooms(data.data);
            } else {
                setAvailableRooms(rooms.filter(r => r.status === 'available'));
            }
        } catch (err) {
            setAvailableRooms(rooms.filter(r => r.status === 'available'));
        }
    };

    const calculatePricing = () => {
        if (!formData.checkInDate || !formData.checkOutDate || !formData.roomId) return;

        const checkIn = new Date(formData.checkInDate);
        const checkOut = new Date(formData.checkOutDate);
        const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));

        if (nights <= 0) return;

        const selectedRoom = rooms.find(r => r.id === parseInt(formData.roomId));
        if (!selectedRoom) return;

        const pricePerNight = parseFloat(selectedRoom.price_per_night) || 0;
        const baseAmount = pricePerNight * nights;

        const gstPercentage = pricePerNight <= 7500 ? 5 : 18;
        const gstAmount = (baseAmount * gstPercentage) / 100;
        const totalAmount = baseAmount + gstAmount;

        setFormData(prev => ({
            ...prev,
            pricePerNight,
            totalNights: nights,
            baseAmount,
            gstPercentage,
            gstAmount,
            totalAmount
        }));
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleRoomChange = (e) => {
        const roomId = e.target.value;
        const selectedRoom = rooms.find(r => r.id === parseInt(roomId));

        setFormData(prev => ({
            ...prev,
            roomId,
            pricePerNight: selectedRoom ? parseFloat(selectedRoom.price_per_night) : 0
        }));
    };

    const validateForm = () => {
        if (!formData.guestName.trim()) {
            setErrorMessage('Guest name is required');
            return false;
        }
        if (!formData.guestPhone.trim()) {
            setErrorMessage('Guest phone number is required');
            return false;
        }
        if (!formData.roomId) {
            setErrorMessage('Please select a room');
            return false;
        }
        if (!formData.checkInDate) {
            setErrorMessage('Check-in date is required');
            return false;
        }
        if (!formData.checkOutDate) {
            setErrorMessage('Check-out date is required');
            return false;
        }
        if (new Date(formData.checkOutDate) <= new Date(formData.checkInDate)) {
            setErrorMessage('Check-out date must be after check-in date');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setSubmitting(true);
        try {
            const token = localStorage.getItem('token');

            const bookingData = {
                roomId: parseInt(formData.roomId),
                guestName: formData.guestName,
                guestPhone: formData.guestPhone,
                guestEmail: formData.guestEmail || null,
                guestNationality: formData.guestNationality,
                guestIdProof: `${formData.guestIdType}: ${formData.guestIdNumber}`,
                numberOfGuests: parseInt(formData.numberOfGuests),
                checkInDate: formData.checkInDate,
                checkOutDate: formData.checkOutDate,
                bookingType: formData.bookingType,
                totalAmount: formData.isComplementary ? 0 : formData.totalAmount,
                baseRoomRate: formData.pricePerNight,
                gstPercentage: formData.gstPercentage,
                roomGstAmount: formData.gstAmount,
                advancePayment: parseFloat(formData.advancePayment) || 0,
                paymentMethod: formData.paymentMethod,
                specialRequests: formData.specialRequests,
                isComplementary: formData.isComplementary,
                status: 'confirmed'
            };

            const response = await fetch(`${PRODUCTION_URL}/api/admin/bookings`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(bookingData)
            });

            const data = await response.json();

            if (data.success) {
                setSuccessMessage('✅ Reservation created successfully!');
                resetForm();
                fetchReservations();
                setActiveTab('list');
            } else {
                setErrorMessage(data.message || 'Failed to create reservation');
            }
        } catch (err) {
            console.error('Error creating reservation:', err);
            setErrorMessage('Error creating reservation. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            guestName: '',
            guestPhone: '',
            guestEmail: '',
            guestNationality: 'Indian',
            guestIdType: 'aadhar',
            guestIdNumber: '',
            numberOfGuests: 1,
            roomId: '',
            checkInDate: '',
            checkOutDate: '',
            bookingType: 'walk_in',
            pricePerNight: 0,
            totalNights: 0,
            baseAmount: 0,
            gstPercentage: 5,
            gstAmount: 0,
            totalAmount: 0,
            advancePayment: 0,
            paymentMethod: 'cash',
            specialRequests: '',
            isComplementary: false
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const getTodayDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    const getStatusBadge = (status) => {
        const statusColors = {
            'confirmed': { bg: '#dbeafe', color: '#1d4ed8', label: 'Confirmed' },
            'checked_in': { bg: '#dcfce7', color: '#16a34a', label: 'Checked In' },
            'checked_out': { bg: '#f3f4f6', color: '#6b7280', label: 'Checked Out' },
            'cancelled': { bg: '#fee2e2', color: '#dc2626', label: 'Cancelled' },
            'pending': { bg: '#fef3c7', color: '#d97706', label: 'Pending' }
        };
        const statusStyle = statusColors[status] || statusColors['pending'];
        return (
            <span style={{
                background: statusStyle.bg,
                color: statusStyle.color,
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600',
                textTransform: 'uppercase'
            }}>
                {statusStyle.label}
            </span>
        );
    };

    // Open payment modal
    const openPaymentModal = (booking) => {
        setSelectedBooking(booking);
        setPaymentData({ amount: '', paymentMethod: 'cash', notes: '' });
        setShowPaymentModal(true);
    };

    // Open cancel modal
    const openCancelModal = (booking) => {
        setSelectedBooking(booking);
        setCancelData({ reason: '', refundMethod: 'cash', processRefund: true });
        setShowCancelModal(true);
    };

    // Record payment
    const handleRecordPayment = async () => {
        if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
            setErrorMessage('Please enter a valid payment amount');
            return;
        }

        setSubmitting(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${PRODUCTION_URL}/api/admin/bookings/${selectedBooking.id}/payment`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    amount: parseFloat(paymentData.amount),
                    paymentMethod: paymentData.paymentMethod,
                    notes: paymentData.notes || 'Advance payment'
                })
            });

            const data = await response.json();
            if (data.success) {
                setSuccessMessage(`✅ Payment of ${formatCurrency(paymentData.amount)} recorded successfully!`);
                setShowPaymentModal(false);
                fetchReservations();
            } else {
                setErrorMessage(data.message || 'Failed to record payment');
            }
        } catch (err) {
            console.error('Error recording payment:', err);
            setErrorMessage('Error recording payment. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    // Cancel booking with refund
    const handleCancelBooking = async () => {
        setSubmitting(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${PRODUCTION_URL}/api/admin/bookings/${selectedBooking.id}/cancel`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    cancelReason: cancelData.reason || 'Customer request',
                    refundMethod: cancelData.refundMethod,
                    processRefund: cancelData.processRefund
                })
            });

            const data = await response.json();
            if (data.success) {
                let msg = '✅ Booking cancelled successfully!';
                if (data.data.refundProcessed) {
                    msg += ` Refund of ${formatCurrency(data.data.refundAmount)} processed.`;
                }
                setSuccessMessage(msg);
                setShowCancelModal(false);
                fetchReservations();
            } else {
                setErrorMessage(data.message || 'Failed to cancel booking');
            }
        } catch (err) {
            console.error('Error cancelling booking:', err);
            setErrorMessage('Error cancelling booking. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    // Filter reservations
    const filteredReservations = reservations.filter(res => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const checkInDate = new Date(res.check_in_date);
        checkInDate.setHours(0, 0, 0, 0);

        // Status filter
        let statusMatch = true;
        if (filterStatus === 'upcoming') {
            statusMatch = checkInDate >= today && res.status !== 'checked_out' && res.status !== 'cancelled';
        } else if (filterStatus === 'confirmed') {
            statusMatch = res.status === 'confirmed';
        } else if (filterStatus === 'checked_in') {
            statusMatch = res.status === 'checked_in';
        }
        // 'all' shows everything

        // Search filter
        let searchMatch = true;
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            searchMatch =
                (res.guest_name?.toLowerCase().includes(query)) ||
                (res.guest_phone?.includes(query)) ||
                (res.room_number?.toString().includes(query)) ||
                (res.booking_reference?.toLowerCase().includes(query));
        }

        return statusMatch && searchMatch;
    });

    // Stats
    const stats = {
        total: reservations.length,
        upcoming: reservations.filter(r => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const checkIn = new Date(r.check_in_date);
            checkIn.setHours(0, 0, 0, 0);
            return checkIn >= today && r.status !== 'checked_out' && r.status !== 'cancelled';
        }).length,
        today: reservations.filter(r => {
            const today = new Date().toISOString().split('T')[0];
            return r.check_in_date?.split('T')[0] === today;
        }).length,
        confirmed: reservations.filter(r => r.status === 'confirmed').length
    };

    return (
        <AdminLayout>
            <div className="reservations-container">
                {/* Success/Error Messages */}
                {successMessage && (
                    <div className="alert alert-success">{successMessage}</div>
                )}
                {errorMessage && (
                    <div className="alert alert-error">{errorMessage}</div>
                )}

                {/* Header */}
                <div className="reservations-header">
                    <div className="header-left">
                        <div>
                            <h1>📅 Reservations</h1>
                            <p>Manage hotel bookings and reservations</p>
                        </div>
                    </div>
                    <button
                        className="btn btn-primary"
                        onClick={() => setActiveTab(activeTab === 'new' ? 'list' : 'new')}
                    >
                        {activeTab === 'new' ? '📋 View Reservations' : '➕ New Reservation'}
                    </button>
                </div>

                {/* Stats Cards */}
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon">📊</div>
                        <div className="stat-value">{stats.total}</div>
                        <div className="stat-label">Total Bookings</div>
                    </div>
                    <div className="stat-card upcoming">
                        <div className="stat-icon">🗓️</div>
                        <div className="stat-value">{stats.upcoming}</div>
                        <div className="stat-label">Upcoming</div>
                    </div>
                    <div className="stat-card today">
                        <div className="stat-icon">📍</div>
                        <div className="stat-value">{stats.today}</div>
                        <div className="stat-label">Today's Arrivals</div>
                    </div>
                    <div className="stat-card confirmed">
                        <div className="stat-icon">✅</div>
                        <div className="stat-value">{stats.confirmed}</div>
                        <div className="stat-label">Confirmed</div>
                    </div>
                </div>

                {activeTab === 'list' ? (
                    /* Reservations List */
                    <div className="reservations-list-section">
                        {/* Filters */}
                        <div className="list-header">
                            <div className="filter-tabs">
                                <button
                                    className={`filter-tab ${filterStatus === 'upcoming' ? 'active' : ''}`}
                                    onClick={() => setFilterStatus('upcoming')}
                                >
                                    Upcoming
                                </button>
                                <button
                                    className={`filter-tab ${filterStatus === 'confirmed' ? 'active' : ''}`}
                                    onClick={() => setFilterStatus('confirmed')}
                                >
                                    Confirmed
                                </button>
                                <button
                                    className={`filter-tab ${filterStatus === 'checked_in' ? 'active' : ''}`}
                                    onClick={() => setFilterStatus('checked_in')}
                                >
                                    Checked In
                                </button>
                                <button
                                    className={`filter-tab ${filterStatus === 'all' ? 'active' : ''}`}
                                    onClick={() => setFilterStatus('all')}
                                >
                                    All
                                </button>
                            </div>
                            <div className="search-box">
                                <input
                                    type="text"
                                    placeholder="Search by name, phone, room..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Reservations Table */}
                        {loading ? (
                            <div className="loading-state">
                                <div className="spinner"></div>
                                <p>Loading reservations...</p>
                            </div>
                        ) : filteredReservations.length === 0 ? (
                            <div className="empty-state">
                                <div className="empty-icon">📭</div>
                                <h3>No reservations found</h3>
                                <p>Create a new reservation to get started</p>
                                <button
                                    className="btn btn-primary"
                                    onClick={() => setActiveTab('new')}
                                >
                                    ➕ New Reservation
                                </button>
                            </div>
                        ) : (
                            <div className="table-container">
                                <table className="reservations-table">
                                    <thead>
                                        <tr>
                                            <th>Guest</th>
                                            <th>Room</th>
                                            <th>Check-in</th>
                                            <th>Check-out</th>
                                            <th>Amount</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredReservations.map((reservation) => (
                                            <tr key={reservation.id}>
                                                <td>
                                                    <div className="guest-info">
                                                        <div className="guest-name">{reservation.guest_name}</div>
                                                        <div className="guest-phone">{reservation.guest_phone}</div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="room-info">
                                                        <span className="room-number">Room {reservation.room_number}</span>
                                                        <span className="room-type">{reservation.room_type}</span>
                                                    </div>
                                                </td>
                                                <td>{formatDate(reservation.check_in_date)}</td>
                                                <td>{formatDate(reservation.check_out_date)}</td>
                                                <td className="amount">{formatCurrency(reservation.total_amount)}</td>
                                                <td>{getStatusBadge(reservation.status)}</td>
                                                <td>
                                                    <div className="action-buttons">
                                                        <button
                                                            className="btn-action view"
                                                            onClick={() => navigate(`/admin/guest-card/${reservation.id}`)}
                                                            title="View Details"
                                                        >
                                                            👁️
                                                        </button>
                                                        {(reservation.status === 'confirmed' || reservation.status === 'pending') && (
                                                            <>
                                                                <button
                                                                    className="btn-action payment"
                                                                    onClick={() => openPaymentModal(reservation)}
                                                                    title="Add Payment"
                                                                >
                                                                    💳
                                                                </button>
                                                                <button
                                                                    className="btn-action cancel"
                                                                    onClick={() => openCancelModal(reservation)}
                                                                    title="Cancel & Refund"
                                                                >
                                                                    ❌
                                                                </button>
                                                            </>
                                                        )}
                                                        {reservation.status === 'checked_in' && (
                                                            <button
                                                                className="btn-action payment"
                                                                onClick={() => openPaymentModal(reservation)}
                                                                title="Add Payment"
                                                            >
                                                                💳
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                ) : (
                    /* New Reservation Form */
                    <form onSubmit={handleSubmit} className="reservation-form">
                        {/* Guest Information Section */}
                        <div className="form-section">
                            <h2>👤 Guest Information</h2>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Guest Name *</label>
                                    <input
                                        type="text"
                                        name="guestName"
                                        value={formData.guestName}
                                        onChange={handleInputChange}
                                        placeholder="Enter full name"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Phone Number *</label>
                                    <input
                                        type="tel"
                                        name="guestPhone"
                                        value={formData.guestPhone}
                                        onChange={handleInputChange}
                                        placeholder="Enter phone number"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Email</label>
                                    <input
                                        type="email"
                                        name="guestEmail"
                                        value={formData.guestEmail}
                                        onChange={handleInputChange}
                                        placeholder="Enter email (optional)"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Nationality</label>
                                    <select
                                        name="guestNationality"
                                        value={formData.guestNationality}
                                        onChange={handleInputChange}
                                    >
                                        <option value="Indian">Indian</option>
                                        <option value="Foreign">Foreign National</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>ID Type *</label>
                                    <select
                                        name="guestIdType"
                                        value={formData.guestIdType}
                                        onChange={handleInputChange}
                                    >
                                        {idTypes.map(type => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>ID Number *</label>
                                    <input
                                        type="text"
                                        name="guestIdNumber"
                                        value={formData.guestIdNumber}
                                        onChange={handleInputChange}
                                        placeholder="Enter ID number"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Number of Guests</label>
                                    <input
                                        type="number"
                                        name="numberOfGuests"
                                        value={formData.numberOfGuests}
                                        onChange={handleInputChange}
                                        min="1"
                                        max="10"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Booking Details Section */}
                        <div className="form-section">
                            <h2>🏨 Booking Details</h2>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Check-in Date *</label>
                                    <input
                                        type="date"
                                        name="checkInDate"
                                        value={formData.checkInDate}
                                        onChange={handleInputChange}
                                        min={getTodayDate()}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Check-out Date *</label>
                                    <input
                                        type="date"
                                        name="checkOutDate"
                                        value={formData.checkOutDate}
                                        onChange={handleInputChange}
                                        min={formData.checkInDate || getTodayDate()}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Booking Type</label>
                                    <select
                                        name="bookingType"
                                        value={formData.bookingType}
                                        onChange={handleInputChange}
                                    >
                                        {bookingTypes.map(type => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group full-width">
                                    <label>Select Room *</label>
                                    <select
                                        name="roomId"
                                        value={formData.roomId}
                                        onChange={handleRoomChange}
                                        required
                                    >
                                        <option value="">-- Select a Room --</option>
                                        {(formData.checkInDate && formData.checkOutDate ? availableRooms : rooms.filter(r => r.status === 'available')).map(room => (
                                            <option key={room.id} value={room.id}>
                                                Room {room.room_number} - {room.room_type} - Floor {room.floor} - {formatCurrency(room.price_per_night)}/night
                                            </option>
                                        ))}
                                    </select>
                                    {formData.checkInDate && formData.checkOutDate && availableRooms.length === 0 && (
                                        <small className="text-warning">No rooms available for selected dates</small>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Pricing Section */}
                        {formData.roomId && formData.totalNights > 0 && (
                            <div className="form-section pricing-section">
                                <h2>💰 Pricing Summary</h2>
                                <div className="pricing-grid">
                                    <div className="pricing-row">
                                        <span>Room Rate</span>
                                        <span>{formatCurrency(formData.pricePerNight)} / night</span>
                                    </div>
                                    <div className="pricing-row">
                                        <span>Number of Nights</span>
                                        <span>{formData.totalNights} night(s)</span>
                                    </div>
                                    <div className="pricing-row">
                                        <span>Base Amount</span>
                                        <span>{formatCurrency(formData.baseAmount)}</span>
                                    </div>
                                    <div className="pricing-row">
                                        <span>GST ({formData.gstPercentage}%)</span>
                                        <span>{formatCurrency(formData.gstAmount)}</span>
                                    </div>
                                    <div className="pricing-row total">
                                        <span>Total Amount</span>
                                        <span>{formData.isComplementary ? 'COMPLIMENTARY' : formatCurrency(formData.totalAmount)}</span>
                                    </div>
                                </div>

                                <div className="form-group checkbox-group">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            name="isComplementary"
                                            checked={formData.isComplementary}
                                            onChange={handleInputChange}
                                        />
                                        Mark as Complimentary Booking (No Charge)
                                    </label>
                                </div>
                            </div>
                        )}

                        {/* Payment Section */}
                        {!formData.isComplementary && formData.totalAmount > 0 && (
                            <div className="form-section">
                                <h2>💳 Payment Details</h2>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Advance Payment</label>
                                        <input
                                            type="number"
                                            name="advancePayment"
                                            value={formData.advancePayment}
                                            onChange={handleInputChange}
                                            min="0"
                                            max={formData.totalAmount}
                                            placeholder="Enter advance amount"
                                        />
                                        <small>Pending: {formatCurrency(formData.totalAmount - (formData.advancePayment || 0))}</small>
                                    </div>
                                    <div className="form-group">
                                        <label>Payment Method</label>
                                        <select
                                            name="paymentMethod"
                                            value={formData.paymentMethod}
                                            onChange={handleInputChange}
                                        >
                                            {paymentMethods.map(method => (
                                                <option key={method.value} value={method.value}>
                                                    {method.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Special Requests */}
                        <div className="form-section">
                            <h2>📋 Additional Information</h2>
                            <div className="form-group full-width">
                                <label>Special Requests / Notes</label>
                                <textarea
                                    name="specialRequests"
                                    value={formData.specialRequests}
                                    onChange={handleInputChange}
                                    rows="3"
                                    placeholder="Any special requests or notes..."
                                />
                            </div>
                        </div>

                        {/* Submit Buttons */}
                        <div className="form-actions">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => setActiveTab('list')}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={submitting}
                            >
                                {submitting ? 'Creating Reservation...' : '✓ Create Reservation'}
                            </button>
                        </div>
                    </form>
                )}
            </div>

            {/* Payment Modal */}
            {showPaymentModal && selectedBooking && (
                <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>💳 Record Payment</h2>
                            <button className="modal-close" onClick={() => setShowPaymentModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="booking-summary">
                                <p><strong>Guest:</strong> {selectedBooking.guest_name}</p>
                                <p><strong>Room:</strong> {selectedBooking.room_number}</p>
                                <p><strong>Total Amount:</strong> {formatCurrency(selectedBooking.total_amount)}</p>
                            </div>
                            <div className="form-group">
                                <label>Payment Amount *</label>
                                <input
                                    type="number"
                                    value={paymentData.amount}
                                    onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                                    placeholder="Enter amount"
                                    min="1"
                                    max={selectedBooking.total_amount}
                                />
                            </div>
                            <div className="form-group">
                                <label>Payment Method</label>
                                <select
                                    value={paymentData.paymentMethod}
                                    onChange={(e) => setPaymentData({ ...paymentData, paymentMethod: e.target.value })}
                                >
                                    <option value="cash">Cash</option>
                                    <option value="card">Card</option>
                                    <option value="upi">UPI</option>
                                    <option value="bank_transfer">Bank Transfer</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Notes</label>
                                <input
                                    type="text"
                                    value={paymentData.notes}
                                    onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                                    placeholder="Advance payment"
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={() => setShowPaymentModal(false)}>Cancel</button>
                            <button className="btn btn-primary" onClick={handleRecordPayment} disabled={submitting}>
                                {submitting ? 'Processing...' : '✓ Record Payment'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Cancel/Refund Modal */}
            {showCancelModal && selectedBooking && (
                <div className="modal-overlay" onClick={() => setShowCancelModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header warning">
                            <h2>⚠️ Cancel Booking</h2>
                            <button className="modal-close" onClick={() => setShowCancelModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="booking-summary">
                                <p><strong>Guest:</strong> {selectedBooking.guest_name}</p>
                                <p><strong>Room:</strong> {selectedBooking.room_number}</p>
                                <p><strong>Total Amount:</strong> {formatCurrency(selectedBooking.total_amount)}</p>
                            </div>
                            <div className="form-group">
                                <label>Cancellation Reason</label>
                                <textarea
                                    value={cancelData.reason}
                                    onChange={(e) => setCancelData({ ...cancelData, reason: e.target.value })}
                                    placeholder="Enter reason for cancellation"
                                    rows="3"
                                />
                            </div>
                            <div className="form-group checkbox-group">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={cancelData.processRefund}
                                        onChange={(e) => setCancelData({ ...cancelData, processRefund: e.target.checked })}
                                    />
                                    Process refund for any advance payments
                                </label>
                            </div>
                            {cancelData.processRefund && (
                                <div className="form-group">
                                    <label>Refund Method</label>
                                    <select
                                        value={cancelData.refundMethod}
                                        onChange={(e) => setCancelData({ ...cancelData, refundMethod: e.target.value })}
                                    >
                                        <option value="cash">Cash</option>
                                        <option value="card">Card</option>
                                        <option value="upi">UPI</option>
                                        <option value="bank_transfer">Bank Transfer</option>
                                    </select>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={() => setShowCancelModal(false)}>Close</button>
                            <button className="btn btn-danger" onClick={handleCancelBooking} disabled={submitting}>
                                {submitting ? 'Processing...' : '❌ Cancel Booking'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default Reservations;
